/* eslint-disable unicorn/consistent-function-scoping -- цепочка middleware как в passport-client */
/**
 * Middleware загрузки статуса registration-survey.
 *
 * @module store/registrationSurvey/fetchRegistrationSurvey.middleware
 */

import { fetchRegistrationSurveyRequest } from '../../requests';
import { FetchError } from '../../requests/sendRequest';
import type { RootAction, RootDispatch, RootState } from '../store.types';
import type { Middleware } from '../store.types';

import {
  FETCH_REGISTRATION_SURVEY_ERROR,
  FETCH_REGISTRATION_SURVEY_START,
  FETCH_REGISTRATION_SURVEY_SUCCESS,
  REGISTRATION_SURVEY_FETCH_ERROR_KEY,
  type RegistrationSurveyPayload,
} from './index';

/**
 * Приводит тело GET `/registration-survey` к виду для стора: пустой JSON/`""`/204 без полей
 * иначе даёт ложный `malformed` и гвард уводит на `/error`, мешая `window.location` после POST.
 *
 * @param {unknown} data — `response.data` из `fetchRegistrationSurveyRequest`
 * @returns {RegistrationSurveyPayload | null}
 */
function normalizeRegistrationSurveyGetPayload(data: unknown): RegistrationSurveyPayload | null {
  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed === '') {
      return { isNeedRunRegistrationSurvey: false };
    }
    try {
      return normalizeRegistrationSurveyGetPayload(JSON.parse(trimmed) as unknown);
    } catch {
      return null;
    }
  }

  if (Array.isArray(data) || typeof data !== 'object') {
    return null;
  }

  const obj = data as Record<string, unknown>;
  if (Object.keys(obj).length === 0) {
    return { isNeedRunRegistrationSurvey: false };
  }

  const snake = obj.is_need_run_registration_survey;
  if (typeof snake === 'boolean') {
    return { isNeedRunRegistrationSurvey: snake };
  }

  const camel = obj.isNeedRunRegistrationSurvey;
  if (typeof camel === 'boolean') {
    return { isNeedRunRegistrationSurvey: camel };
  }

  return null;
}

export const fetchRegistrationSurveyMiddleware: Middleware<unknown, RootState, RootDispatch> =
  (_api) => (next) => async (action: RootAction) => {
    if (action.type !== FETCH_REGISTRATION_SURVEY_START) {
      return next(action);
    }

    const payload = action.payload;
    if (payload === null || typeof payload !== 'object' || !Object.hasOwn(payload, 'token')) {
      return next(action);
    }
    const tokenRecord = payload as Record<string, unknown>;
    const tokenVal = tokenRecord.token;
    if (typeof tokenVal !== 'string') {
      return next(action);
    }
    const token = tokenVal;

    next(action);

    try {
      const response = await fetchRegistrationSurveyRequest({ token });
      const surveyPayload = normalizeRegistrationSurveyGetPayload(response.data);
      return next({ type: FETCH_REGISTRATION_SURVEY_SUCCESS, payload: surveyPayload });
    } catch (error) {
      const err =
        error instanceof FetchError
          ? error
          : new FetchError({
              message: error instanceof Error ? error.message : String(error),
              status: 0,
              statusText: '',
              headers: {},
              payload: error,
            });
      return next({
        type: FETCH_REGISTRATION_SURVEY_ERROR,
        payload: { [REGISTRATION_SURVEY_FETCH_ERROR_KEY]: err },
      });
    }
  };
